import React, { useContext, useEffect, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import * as Sentry from '@sentry/react';
import { DEFAULT_PAGE_SIZE, STAFF_ROLES_CACHE_KEY } from '../constants';
import { PageMetadata } from '../components/datagridComponents/paginationHelpers';
import { ApiContext } from '../context/ApiContext';
import { SelectOption, StaffRole } from '../types';
import { skipSentry } from '../helpers/helpers';
import { API_V1_HOSTNAME_KEY } from '../localStorageKeys';

const useStaffRolesOptions = () => {
  const { apiFetch } = useContext(ApiContext);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [isLoadingStaffRoles, setIsLoadingStaffRoles] = useState<boolean>(false);

  const {
    isLoading,
    isRefetching,
    refetch: fetchStaffRolesOptions,
    fetchNextPage,
    hasNextPage,
    data,
  } = useInfiniteQuery<{ data: StaffRole[]; metadata: PageMetadata }>({
    queryKey: [STAFF_ROLES_CACHE_KEY],
    queryFn: ({ pageParam = 1 }) =>
      apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/staff-roles?limit=${DEFAULT_PAGE_SIZE}&page=${pageParam}`),
    getNextPageParam: (lastPage) => {
      if (
        lastPage?.metadata &&
        lastPage?.metadata.page &&
        lastPage?.metadata.totalPages &&
        lastPage.metadata.page < lastPage.metadata.totalPages
      ) {
        return lastPage.metadata.page + 1;
      }

      return null;
    },
    onError: (err: any) => {
      if (err?.code === 401) return;
      if (!skipSentry(err)) {
        Sentry.captureException(new Error('Error fetching Staff roles'), {
          extra: { error: JSON.stringify(err) },
        });
      }
      setIsLoadingStaffRoles(false);
    },
  });

  useEffect(() => {
    if (isLoading) {
      setIsLoadingStaffRoles(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!data) return;

    if (hasNextPage) {
      fetchNextPage();
    } else {
      let staffRolesOptions: SelectOption[] = [];
      if (data.pages && Array.isArray(data.pages)) {
        for (let i = 0; i < data.pages.length; i += 1) {
          staffRolesOptions = staffRolesOptions.concat(
            data.pages[i].data.map((role) => ({
              label: role.name,
              value: role.id?.toString(),
            }))
          );
        }
      }

      setRoleOptions(staffRolesOptions);
      setIsLoadingStaffRoles(false);
    }
  }, [data]);

  return {
    staffRolesOptions: roleOptions,
    fetchStaffRolesOptions,
    isLoadingStaffRolesOptions: isLoading || isLoadingStaffRoles,
    isRefetching,
  };
};

export default useStaffRolesOptions;
